import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useEffect,
  SetStateAction,
  Dispatch,
  useCallback,
} from "react";
import {
  IProductMinimizedData,
  IProductChildMinimizedData,
} from "@/app/components/pages-components/product-details-page/details-belpers/product-attributes/helpers/interfaces";
import { intersectionsArray } from "@/app/components/common-addons-builder/addon-functions";
import { useGetCartQuery } from "@/api/AppApi/services/cart";
import { ICartItemType } from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { getIsService } from "../product-box-helpers/functions";
import {
  ProductPreviewTypes,
  unit,
} from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import { compareAddOnsArrays } from "@/hooks/product-has-attrs/product-has-attrs-helpers/functions";

interface ProductBoxContextType {
  selectedChild: IProductChildMinimizedData | IProductMinimizedData;
  selectedChildIndex: number | null;
  setSelectedChildIndex: (index: number | null) => void;
  isSelectedChild: boolean;
  product: IProductMinimizedData;
  attrs_values: number[];
  quantity: number;
  setQuantity: Dispatch<SetStateAction<number>>;
  addedToCart: boolean;
  setAddedToCart: Dispatch<SetStateAction<boolean>>;
  quick_variant_selection?: boolean;
  showselectAttrsPopup: boolean;
  selectedAttributes: number[];
  setSelectedAttributes: Dispatch<SetStateAction<number[]>>;
  outOfStock: boolean;
  viewDetails: boolean;
  mustViewDetails?: boolean;
  currentProductInCart: {
    quantity: number;
    addedToCart: boolean;
    id?: number;
  };
}

const ProductBoxContext = createContext<ProductBoxContextType | undefined>(
  undefined,
);

const getProductFromCart = (
  cartItems: ICartItemType[],
  product: IProductMinimizedData | IProductChildMinimizedData,
) => {
  const index = (cartItems ?? [])?.findIndex(
    (item) =>
      item.inner_id === product?.id && compareAddOnsArrays(item.addons, []),
  );
  if (index < 0)
    return {
      quantity: 0,
      addedToCart: false,
      id: undefined,
    };
  return {
    quantity: cartItems[index].quantity,
    addedToCart: true,
    id: cartItems[index].id,
  };
};
export const ProductBoxContextProvider: React.FC<{
  children: ReactNode;
  product: IProductMinimizedData;
  preview_type: ProductPreviewTypes;
  units: unit[];
  type: string;
  bundleSetSelectedChildIndex?: (index: number | null) => void;
}> = ({
  children,
  product,
  preview_type,
  units,
  type,
  bundleSetSelectedChildIndex,
}) => {
  const getCart = useGetCartQuery();
  const compnay = useGetCompanyQuery();
  const { quick_variant_selection, show_all_product_units } =
    compnay.data?.result.theme.components.product ?? {};
  const cartItems = getCart?.data?.result?.items;
  const [selectedChildIndex, setSelectedChildIndex] = useState<number | null>(
    null,
  );
  const [selectedAttributes, setSelectedAttributes] = useState<number[]>([]);
  const [quantity, setQuantity] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);
  const selectedChild = useMemo(() => {
    if (selectedChildIndex === null || selectedChildIndex === -1) {
      return product;
    }

    const child = product?.children?.[selectedChildIndex];
    if (!child || !product) return product;

    return {
      ...child,
      // discount_amount: child.discount_amount ?? product.discount_amount,
      discount_type: child.discount_type ?? product.discount_type,
      discount: child.discount ?? product.discount,
      is_new: child.is_new ?? product.is_new,
      images: child.images?.length ? child.images : product.images,
      thumbnail_url: child.thumbnail_url ?? product.thumbnail_url,
      price: child.price ?? product.price,
      price_without_discount:
        child.price_without_discount ?? product.price_without_discount,
      selling_prices: child.selling_prices ?? product.selling_prices,
      has_required_addons:
        child.has_required_addons ?? product.has_required_addons,
      has_discount: child.has_discount ?? product.has_discount,
    };
  }, [product, selectedChildIndex]);

  const currentProductInCart = getProductFromCart(
    cartItems ?? [],
    selectedChild,
  );

  const resetProduct = useCallback(() => {
    setAddedToCart(currentProductInCart.addedToCart);
    setQuantity(currentProductInCart.quantity);
  }, [currentProductInCart.addedToCart, currentProductInCart.quantity]);

  useEffect(() => {
    if (getCart.isFetching) return;
    resetProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCart?.isFetching, selectedChild?.id, cartItems, resetProduct]);
  useEffect(() => {
    setQuantity(currentProductInCart.quantity);
  }, [currentProductInCart.quantity]);

  const isService = getIsService(preview_type as ProductPreviewTypes);
  const isBundle = type === "bundle";
  const mustViewDetails =
    isService ||
    isBundle ||
    selectedChild.has_required_addons ||
    selectedChild.has_term_and_condition ||
    selectedChild.has_required_fields ||
    (!!units.length && show_all_product_units);

  const viewDetails =
    mustViewDetails ||
    selectedChild.is_configurable ||
    selectedChild.has_configurable_parent;

  const outOfStock =
    (selectedChild.quantity_on_hand - currentProductInCart.quantity <= 0 ||
      (!!selectedChild.min_quantity_in_cart &&
        selectedChild.min_quantity_in_cart >
          selectedChild.quantity_on_hand - currentProductInCart.quantity)) &&
    !viewDetails;
  const handleSelectedChildIndex = useCallback(
    (index: number | null) => {
    setSelectedChildIndex(index);
      if (!!bundleSetSelectedChildIndex) bundleSetSelectedChildIndex(index);
    },
    [bundleSetSelectedChildIndex],
  );
  const value = useMemo(
    () => ({
      selectedChild,
      selectedChildIndex,
      setSelectedChildIndex: handleSelectedChildIndex,
      isSelectedChild: selectedChildIndex !== null && selectedChildIndex >= 0,
      product,
      attrs_values: intersectionsArray(
        product.attribute_ids,
        selectedChild?.attribute_ids,
      ),
      quantity,
      setQuantity,
      addedToCart,
      setAddedToCart,
      quick_variant_selection,
      selectedAttributes,
      setSelectedAttributes,
      showselectAttrsPopup:
        product?.attributes_builder?.length > 0 &&
        !selectedChildIndex &&
        selectedAttributes?.length > 0,
      outOfStock,
      viewDetails,
      mustViewDetails,
      currentProductInCart,
    }),
    [
      selectedChild,
      selectedChildIndex,
      handleSelectedChildIndex,
      product,
      quantity,
      addedToCart,
      quick_variant_selection,
      selectedAttributes,
      outOfStock,
      viewDetails,
      mustViewDetails,
      currentProductInCart,
    ],
  );

  return (
    <ProductBoxContext.Provider value={value}>
      {children}
    </ProductBoxContext.Provider>
  );
};

export const useProductBox = (): ProductBoxContextType => {
  const context = useContext(ProductBoxContext);
  if (context === undefined) {
    throw new Error("useProductBox must be used within a ProductBoxProvider");
  }
  return context;
};
